import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, AlertTriangle, X, Save } from 'lucide-react';
import {
    contratoService,
    ContratoItem,
    ContratoItemInput,
} from '../../../../services/contratoService';
import { produtoServicoService, ProdutoServico } from '../../../../services/propostaService';

interface ContratoItensProps {
    contratoId: string;
}

function formatarMoeda(valor: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
}

const defaultForm: ContratoItemInput = {
    produtoServicoId: null,
    descricao: '',
    quantidade: 1,
    valorUnit: 0,
    tipoValor: 'fixo',
    ordem: 0,
};

export function ContratoItens({ contratoId }: ContratoItensProps) {
    const [itens, setItens] = useState<ContratoItem[]>([]);
    const [produtos, setProdutos] = useState<ProdutoServico[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<ContratoItemInput>(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        carregarItens();
        carregarProdutos();
    }, [contratoId]);

    async function carregarItens() {
        try {
            setLoading(true);
            const data = await contratoService.listItens(contratoId);
            setItens(data);
        } catch (err) {
            console.error('Erro ao carregar itens:', err);
        } finally {
            setLoading(false);
        }
    }

    async function carregarProdutos() {
        try {
            const data = await produtoServicoService.list({ active: true });
            setProdutos(data);
        } catch (err) {
            console.error('Erro ao carregar produtos/serviços:', err);
        }
    }

    function abrirModalNovo() {
        setEditingId(null);
        setForm(defaultForm);
        setError(null);
        setShowModal(true);
    }

    function abrirModalEditar(item: ContratoItem) {
        setEditingId(item.id);
        setForm({
            produtoServicoId: item.produtoServicoId,
            descricao: item.descricao,
            quantidade: Number(item.quantidade),
            valorUnit: Number(item.valorUnit),
            tipoValor: item.tipoValor,
            ordem: item.ordem,
        });
        setError(null);
        setShowModal(true);
    }

    function handleProdutoChange(produtoId: string) {
        if (!produtoId) {
            setForm(f => ({ ...f, produtoServicoId: null }));
            return;
        }
        const produto = produtos.find(p => p.id === Number(produtoId));
        if (produto) {
            setForm(f => ({
                ...f,
                produtoServicoId: produto.id,
                descricao: produto.nome, // Sempre preencher com o nome do produto
                valorUnit: Number(produto.valorUnit), // Usar valorUnit como preço padrão
            }));
        }
    }

    async function handleSalvar() {
        if (!form.descricao.trim()) {
            setError('Descrição é obrigatória.');
            return;
        }
        if (form.valorUnit <= 0) {
            setError('Valor unitário deve ser maior que zero.');
            return;
        }
        try {
            setSaving(true);
            setError(null);
            if (editingId) {
                await contratoService.updateItem(contratoId, editingId, form);
            } else {
                await contratoService.createItem(contratoId, form);
            }
            setShowModal(false);
            await carregarItens();
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erro ao salvar item.');
        } finally {
            setSaving(false);
        }
    }

    async function handleExcluir(item: ContratoItem) {
        if (!confirm(`Excluir item "${item.descricao}"?`)) return;
        try {
            await contratoService.deleteItem(contratoId, item.id);
            await carregarItens();
        } catch (err) {
            console.error('Erro ao excluir item:', err);
        }
    }

    const totalMensal = itens.reduce(
        (acc, it) => acc + Number(it.quantidade) * Number(it.valorUnit),
        0
    );

    return (
        <div className="bg-card rounded-lg shadow-sm p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-medium text-foreground">Itens do Contrato</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Gabarito para geração automática de faturas mensais
                    </p>
                </div>
                <button
                    type="button"
                    onClick={abrirModalNovo}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm"
                >
                    <Plus className="w-4 h-4" />
                    Adicionar Item
                </button>
            </div>

            {/* Tabela */}
            {loading ? (
                <div className="flex items-center justify-center h-24">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
            ) : itens.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">
                    Nenhum item cadastrado. Adicione os serviços que compõem este contrato.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-border">
                                <th className="pb-2 text-muted-foreground font-medium">Descrição</th>
                                <th className="pb-2 text-muted-foreground font-medium text-center">SKU</th>
                                <th className="pb-2 text-muted-foreground font-medium text-center">Qtd</th>
                                <th className="pb-2 text-muted-foreground font-medium text-right">Valor Unit.</th>
                                <th className="pb-2 text-muted-foreground font-medium text-right">Subtotal</th>
                                <th className="pb-2 text-muted-foreground font-medium text-center">Tipo</th>
                                <th className="pb-2 w-16" />
                            </tr>
                        </thead>
                        <tbody>
                            {itens.map(item => (
                                <tr key={item.id} className="border-b border-border/50 hover:bg-muted/30">
                                    <td className="py-2 pr-2">
                                        <span className="font-medium">{item.descricao}</span>
                                    </td>
                                    <td className="py-2 text-center text-muted-foreground font-mono text-xs">
                                        {item.produtoServico?.codigo || '—'}
                                    </td>
                                    <td className="py-2 text-center">{Number(item.quantidade)}</td>
                                    <td className="py-2 text-right">{formatarMoeda(Number(item.valorUnit))}</td>
                                    <td className="py-2 text-right">
                                        {formatarMoeda(Number(item.quantidade) * Number(item.valorUnit))}
                                    </td>
                                    <td className="py-2 text-center">
                                        {item.tipoValor === 'variavel' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                                                <AlertTriangle className="w-3 h-3" />
                                                Variável
                                            </span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                Fixo
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                type="button"
                                                onClick={() => abrirModalEditar(item)}
                                                className="p-1 text-muted-foreground hover:text-foreground rounded"
                                                title="Editar"
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleExcluir(item)}
                                                className="p-1 text-muted-foreground hover:text-red-600 rounded"
                                                title="Excluir"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={4} className="pt-3 text-sm text-muted-foreground">
                                    Total mensal (fixos)
                                </td>
                                <td className="pt-3 text-right font-semibold text-foreground">
                                    {formatarMoeda(totalMensal)}
                                </td>
                                <td colSpan={2} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* Modal Add/Edit */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-foreground">
                                {editingId ? 'Editar Item' : 'Novo Item'}
                            </h4>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="p-1 hover:bg-muted rounded"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Produto/Serviço (Catálogo) */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Selecionar do Catálogo
                                </label>
                                <select
                                    value={form.produtoServicoId ?? ''}
                                    onChange={e => handleProdutoChange(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg text-foreground bg-card focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                >
                                    <option value="">— Outro / Serviço Avulso —</option>
                                    {produtos.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.codigo ? `[${p.codigo}] ` : ''}{p.nome} — {formatarMoeda(Number(p.valorUnit))}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    💡 Selecione um item do catálogo para preencher automaticamente nome e preço
                                </p>
                            </div>

                            {/* Descrição */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Descrição *
                                </label>
                                <input
                                    type="text"
                                    value={form.descricao}
                                    onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-lg text-foreground bg-card focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    placeholder="Ex: Gestão de TI Mensal"
                                />
                            </div>

                            {/* Quantidade + Valor Unit */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Quantidade
                                    </label>
                                    <input
                                        type="number"
                                        step="any"
                                        min="0"
                                        value={form.quantidade}
                                        onChange={e => setForm(f => ({ ...f, quantidade: parseFloat(e.target.value) || 0 }))}
                                        className="w-full px-3 py-2 border rounded-lg text-foreground bg-card focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        placeholder="Ex: 1.85"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        💡 Aceita valores fracionados (ex: 1.85)
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Valor Unitário (R$) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.valorUnit}
                                        onChange={e => setForm(f => ({ ...f, valorUnit: parseFloat(e.target.value) || 0 }))}
                                        className="w-full px-3 py-2 border rounded-lg text-foreground bg-card focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        💰 Você pode ajustar o valor para aplicar descontos ou acréscimos
                                    </p>
                                </div>
                            </div>

                            {/* Tipo de valor */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Tipo de Valor
                                </label>
                                <div className="flex gap-4">
                                    {(['fixo', 'variavel'] as const).map(tipo => (
                                        <label key={tipo} className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="tipoValor"
                                                value={tipo}
                                                checked={form.tipoValor === tipo}
                                                onChange={() => setForm(f => ({ ...f, tipoValor: tipo }))}
                                                className="accent-primary"
                                            />
                                            <span className="text-sm capitalize">
                                                {tipo === 'fixo' ? 'Fixo (quantidade constante)' : 'Variável (ajustar mensalmente)'}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                {form.tipoValor === 'variavel' && (
                                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        Itens variáveis serão criados na fatura com a quantidade cadastrada aqui como valor inicial.
                                    </p>
                                )}
                            </div>

                            {error && (
                                <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-3 py-2">
                                    {error}
                                </p>
                            )}
                        </div>

                        {/* Ações */}
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-foreground hover:bg-muted rounded-lg text-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSalvar}
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm"
                            >
                                <Save className="w-4 h-4" />
                                {saving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

